'use client';

import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { reportMykissClientError } from '@lib/errors/client';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { getPagePosts } from '@graphql/queries/rutilus/Page';

import { Heading } from '../../../components/Heading/Heading';
import { PostsMasonry } from '../../../components/PostsMasonry/PostsMasonry';
import { Post } from '../../../components/PostsViewer/types/Post';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { GetPagePosts } from '../../../generated/graphql';

interface IProps {
  pageId: string;
  pageName: string;
}

export const PageFeed = ({ pageId, pageName }: IProps) => {
  const clientHasRendered = useClientHasRendered();

  const { data, loading, fetchMore } = useRutilusQuery(getPagePosts, {
    variables: { id: pageId },
  });

  const posts = getNodesFromConnection<Post>(data?.entry.posts);
  const hasMorePosts = data?.entry.posts.pageInfo.hasNextPage ?? false;
  const endCursor = data?.entry.posts.pageInfo.endCursor ?? null;

  const fetchNextPage = async (): Promise<unknown> =>
    fetchMore({
      variables: { id: pageId, after: endCursor },
      updateQuery: (prev, { fetchMoreResult }): GetPagePosts => {
        return {
          ...fetchMoreResult,
          entry: {
            ...fetchMoreResult.entry,
            posts: {
              ...fetchMoreResult.entry.posts,
              edges: [
                ...(prev.entry.posts.edges ?? []),
                ...(fetchMoreResult.entry.posts.edges ?? []),
              ],
            },
          },
        };
      },
    }).catch(reportMykissClientError);

  if (!loading && clientHasRendered && posts.length === 0) {
    return <></>;
  }

  return (
    <>
      <Heading variant="sm">Posts</Heading>
      <PostsMasonry
        posts={posts}
        fetchMore={fetchNextPage}
        hasMore={hasMorePosts}
        appDownloadModalHeader={`See all catches and posts from ${pageName} in the Fishbrain app`}
        appDownloadModalText="Download the app to see more info about your favorite anglers, ability to follow them and see what they catch next."
      />
    </>
  );
};
